import { useState } from "react";
import { Button, Textarea, TimePicker, cn } from "@carry/ui";
import { createDriverBackfillRequest, type DriverFetcher } from "../hooks";

/** "HH:mm" 以外 (未設定・formatHm の "—" センチネル) は既定値へ落とす。 */
function normalizeHint(hint: string | null | undefined, fallback: string): string {
  return hint && hint !== "—" ? hint : fallback;
}

/**
 * #817/#839/#842: 過去日の stop に、到着・完了時刻と理由を後から登録・修正するインラインフォーム。
 * 過去日報の配送明細 (HistoryScreen) と stop 詳細 (StopDetail) の両方から呼ばれる共有部品。
 * 保存は `createDriverBackfillRequest` (保存時点で実績へ即時反映) を直接叩くだけで、
 * 日報や勤怠の存在を前提にしない。
 *
 * `mode: "correct"` は #842: 差し戻された日報の完了済み stop 向け。データが無いのではなく
 * 「既にある値が間違っている」ケースなので、文言を「登録」ではなく「修正」にし、
 * 既存の到着・完了時刻をそれぞれ独立してプリフィルする (どちらか一方だけ間違っていることが多い)。
 */
export function IncompleteStopBackfillForm({
  stopId,
  isPickup,
  stopName,
  workDate,
  /** 既存の到着実績 (無ければ null)。"HH:mm" 形式のヒント。 */
  initialArrivalHint,
  /** 既存の完了実績 (無ければ null)。"HH:mm" 形式のヒント。 */
  initialDepartureHint,
  mode = "backfill",
  fetcher,
  onSaved,
}: {
  stopId: string;
  /** ラベル分岐用。集荷系 stop は「集荷完了」、それ以外は「配送完了」と出す。 */
  isPickup: boolean;
  stopName: string | null;
  /** 対象日 (YYYY-MM-DD)。システム時刻ではなくこの日付で確定させる。 */
  workDate: string;
  initialArrivalHint?: string | null;
  initialDepartureHint?: string | null;
  /** "backfill"=未完了 stop への新規登録 (既定) / "correct"=差し戻された日報の完了済み stop の修正 (#842) */
  mode?: "backfill" | "correct";
  fetcher: DriverFetcher;
  onSaved: () => void | Promise<void>;
}) {
  const [arrivalHhmm, setArrivalHhmm] = useState(normalizeHint(initialArrivalHint, "09:00"));
  const [departureHhmm, setDepartureHhmm] = useState(normalizeHint(initialDepartureHint, "09:00"));
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reasonEmpty = reason.trim().length === 0;
  const reasonTooLong = reason.length > 1000;
  const timeOrderInvalid = departureHhmm < arrivalHhmm;
  const submitDisabled = submitting || reasonEmpty || reasonTooLong || timeOrderInvalid;

  async function onSubmit() {
    if (submitDisabled) return;
    setError(null);
    setSubmitting(true);
    try {
      await createDriverBackfillRequest(fetcher, stopId, {
        actual_arrival: `${workDate}T${arrivalHhmm}:00+09:00`,
        actual_departure: `${workDate}T${departureHhmm}:00+09:00`,
        reason: reason.trim(),
      });
      await onSaved();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "登録に失敗しました");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mt-2 space-y-2 rounded-lg bg-[var(--bg-sunken)] p-2.5">
      <div className="text-[length:var(--fs-xs)] font-semibold text-[var(--ink-2)]">
        {mode === "correct" ? "実績を修正する" : "実績を後から登録"}
      </div>
      <div className="flex gap-2">
        <label className="flex flex-1 flex-col gap-1 text-[length:var(--fs-xs)] text-[var(--ink-3)]">
          到着
          <TimePicker
            value={arrivalHhmm}
            onChange={setArrivalHhmm}
            disabled={submitting}
            aria-label={`${stopName ?? "配送先"}の到着時刻`}
            className="w-full justify-center"
          />
        </label>
        <label className="flex flex-1 flex-col gap-1 text-[length:var(--fs-xs)] text-[var(--ink-3)]">
          {isPickup ? "集荷完了" : "配送完了"}
          <TimePicker
            value={departureHhmm}
            onChange={setDepartureHhmm}
            disabled={submitting}
            aria-label={`${stopName ?? "配送先"}の完了時刻`}
            className="w-full justify-center"
          />
        </label>
      </div>
      {timeOrderInvalid && (
        <div className="text-[length:var(--fs-xs)] text-[var(--danger)]">
          到着時刻は完了時刻より前にしてください
        </div>
      )}
      <label className="flex flex-col gap-1 text-[length:var(--fs-xs)] text-[var(--ink-3)]">
        理由 (必須)
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          placeholder="例: 当日は圏外で打刻できなかったため"
          aria-label={`${stopName ?? "配送先"}の後から登録する理由`}
        />
      </label>
      {error && (
        <div
          role="alert"
          className="rounded-lg bg-[var(--st-cancel-bg)] px-2 py-1.5 text-[length:var(--fs-xs)] text-[var(--st-cancel-ink)]"
        >
          ⚠ {error}
        </div>
      )}
      <Button
        type="button"
        onClick={() => void onSubmit()}
        disabled={submitDisabled}
        className={cn("w-full", submitting && "cursor-wait opacity-70")}
      >
        {mode === "correct"
          ? submitting
            ? "修正中…"
            : "実績を修正する"
          : submitting
            ? "登録中…"
            : "実績を登録する"}
      </Button>
    </div>
  );
}
